import { createContext, useContext, useMemo } from "react";
import { useMatches } from "react-router";

import type { ProductItem } from "~/lib/app.types";

/** Surface a line item renders in — drives its test-id scope so a `/cart`
 *  locator doesn't also match the minicart popover / edit dialog row. */
export type BasketLineItemSurface =
  | "cart"
  | "minicart"
  | "edit-dialog"
  | "ribbon";

const Context = createContext<{
  handle: any;
  boundryRef: React.RefObject<HTMLElement>;
  lineItem: ProductItem;
  isStale: boolean;
  setIsStale: (isStale: boolean) => void;
  /** Test-id prefix for this surface, e.g. `cart-item` / `minicart-item`. */
  idScope: string;
  /** Row index normalized to BasketRowsContext row order, not surface display order. */
  rowIndex?: number;
}>(null as any);

export const useBasketLineItem = () => {
  const ctx = useContext(Context);
  if (!ctx)
    throw new Error(
      "useBasketLineItem must be used inside a BasketLineItemProvider",
    );
  return ctx;
};

export const useBasketHandle = () => {
  const matches = useMatches();
  const match = useMemo(
    () =>
      matches
        .reverse()
        .filter(match => (match as any).handle?.["basketLineItem"])
        .pop(),
    [matches],
  );
  const handle = (match as any)?.handle?.["basketLineItem"];
  if (typeof handle === "function") {
    return handle((match as any).data);
  }
  return handle;
};

export { Context as BasketLineItemContext };
